import { useEffect } from 'react';

import type { FindPaymentsRequestParams } from '~domains/commerce/transactions/entities/payment';
import { useGenerateReportMutation } from '~domains/commerce/transactions/entities/payment';
import { isReportGenerated } from '~domains/commerce/transactions/entities/payment/lib/is-report-generated';
import {
  useCheckReportStatusQuery,
  useDownloadReportQuery,
} from '~domains/commerce/transactions/entities/report';

import { useHasPermissions } from '~platform/auth';

import { ampli } from '~shared/analytics';

import { Button } from '@breezeventures/ui-kit/button';
import { Icon } from '@breezeventures/ui-kit/next/icon';

export const PaymentsExport = ({
  params,
  disabled,
  size = 'm',
}: {
  params?: FindPaymentsRequestParams;
  disabled?: boolean;
  size?: 's' | 'm';
}) => {
  const { checkPermissions } = useHasPermissions();
  const canExport = checkPermissions(['TRANSACTIONS_EXPORT']);
  const generateReportMutation = useGenerateReportMutation();
  const checkStatusQuery = useCheckReportStatusQuery(
    generateReportMutation.data?.reportId
  );
  const downloadQuery = useDownloadReportQuery({
    reportData: checkStatusQuery.data,
    screen: 'payments',
  });

  useEffect(() => {
    if (downloadQuery.isSuccess) {
      generateReportMutation.reset();
    }
  }, [downloadQuery.isSuccess, generateReportMutation]);

  if (!canExport) {
    return null;
  }

  const handleExportClick = () => {
    if (!params) return;
    generateReportMutation.mutate(params);
    ampli.paymentsScreenTapExport();
  };
  const reportGenerationInProgress =
    generateReportMutation.isSuccess &&
    !isReportGenerated(checkStatusQuery.data);
  const downloadInProgress =
    isReportGenerated(checkStatusQuery.data) && downloadQuery.isLoading;

  const showLoader =
    generateReportMutation.isPending ||
    reportGenerationInProgress ||
    downloadInProgress;

  return (
    <Button
      type="button"
      size={size}
      variant="secondary"
      loading={showLoader}
      disabled={showLoader || disabled || !params}
      onClick={handleExportClick}
    >
      {!showLoader && (
        <Button.Icon>
          <Icon name="bracket-down-arrow-down" size={24} version="v1" />
        </Button.Icon>
      )}
      Export to Excel
    </Button>
  );
};
